/**
 * One person, before the demo and after it.
 *
 * The report says nineteen moved up; this is one of the nineteen, with
 * the working shown. Every figure on that block is derived — from
 * registration and payment dates, not from anything typed after a demo —
 * so the one thing this owes a reader is the evidence: the dates it read,
 * the day of the demo it compared them against, and what somebody said
 * about it if anybody said anything.
 */
import { useApp } from '../../store/AppContext.jsx';
import { parseDate } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import { Avatar, Pill, Dash, EmptyState } from '../Primitives.jsx';
import DealJourney from '../DealJourney.jsx';
import { demoShift, rungLabel, RUNGS } from '../../domain/demoShift.js';
import { stamp } from '../../domain/demos.js';

const tone = key => (RUNGS.find(r => r.key === key) || RUNGS[0]).tone;

/** One fact and where it came from — the point of the whole sheet. */
const Fact = ({ label, children, note }) => (
  <div className="dsvfact">
    <dt>{label}</dt>
    <dd>{children ?? <Dash />}</dd>
    {note ? <small>{note}</small> : null}
  </div>
);

export default function DemoShiftView({ id, onClose }) {
  const { demos, rows, fmtDate, fmtMoney } = useApp();
  const b = (demos?.bookings || []).find(x => x.id === id);

  if (!b) {
    return (
      <Sheet icon="users" tone="g-blue" title="Demo" onClose={onClose} closeLabel="Close" wide>
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon="alert" tone="g-slate" title="Not here any more">
            This booking is no longer in the loaded list — it may have been refreshed
            since the report drew.
          </EmptyState>
        </div>
      </Sheet>
    );
  }

  const s = b.session || {};
  const x = demoShift(rows, b);
  const c = x.client;
  const get = (...k) => k.map(n => c?.[n]).find(v => v !== undefined && v !== null && v !== '') || '';

  /*
   * A date field does not always hold a date.
   *
   * The ledger writes the literal "No trial" into trialEnd for a
   * thousand accounts, and `new Date('No trial')` is an Invalid Date —
   * which used to crash this sheet on open. parseDate says no rather
   * than lying, and what it cannot read is shown as written, because
   * "No trial" is a real answer and a dash is not.
   */
  const day = v => {
    if (!v) return null;
    const d = parseDate(v);
    return d ? fmtDate(d) : String(v);
  };

  return (
    <Sheet
      icon="users"
      tone="g-blue"
      title={c?.name || b.invitee?.name || b.invitee?.email || 'Demo'}
      onClose={onClose}
      closeLabel="Close"
      wide
      bodyClass="dsv"
      hero={(
        <div className="dsvhero">
          <Avatar name={c?.name || b.invitee?.name || '?'} className="dsvava" />
          <div className="dsvmove">
            <Pill tone={tone(x.before)}>{rungLabel(x.before)}</Pill>
            <Icon name="chev" />
            <Pill tone={tone(x.after)}>{rungLabel(x.after)}</Pill>
          </div>
          <div className="dsvwhen">
            <b>{stamp(b.start)}</b>
            <small>the demo this is measured from</small>
          </div>
        </div>
      )}
    >
      {/* What somebody wrote down beats anything worked out from dates, so
          it goes first where it exists. */}
      {x.said ? (
        <section className="vpanel wide">
          <h4><span className="ic g-green"><Icon name="check" /></span>What sales recorded</h4>
          <div className="vbody">
            <p className="dsvsaid">
              <b>{x.said}</b>
              {x.saidWhy ? <span>“{x.saidWhy}”</span> : null}
            </p>
          </div>
        </section>
      ) : null}

      <section className="vpanel wide">
        <h4><span className="ic g-cyan"><Icon name="chart" /></span>The dates this was read from</h4>
        <div className="vbody">
          <dl className="dsvfacts">
            <Fact label="Registered" note={x.before === 'none' ? 'after the demo' : 'before the demo'}>
              {day(get('reg_on', 'regOn'))}
            </Fact>
            <Fact label="Trial ends">{day(get('trial_end', 'trialEnd'))}</Fact>
            <Fact label="Last paid" note={get('pay_status', 'pay') ? `pay status ${get('pay_status', 'pay')}` : null}>
              {day(get('last_paid', 'lastPaid'))}
            </Fact>
            <Fact label="Next due">{day(get('next_due', 'nextDue'))}</Fact>
            {/* Both sides, named. One "Plan" row could only ever hold
                today's, and a reader comparing a before with an after
                should not have to work out which one they are looking at. */}
            <Fact
              label="Plan before the demo"
              note={x.planBefore ? 'recorded when we matched them' : null}
            >
              {x.planBefore || (
                <span className="dim">
                  {x.before === 'none' ? 'not a client, so no plan' : 'never recorded'}
                </span>
              )}
            </Fact>
            {/* Read now, and said to be: they can upgrade whenever they are
                ready, so this moves on its own and the note says so rather
                than letting a stale-looking figure be read as final. */}
            <Fact
              label="Plan now"
              note={x.planMoved ? 'changed since the demo' : 'as it stands today'}
            >
              {x.plan || <span className="dim">no plan</span>}
            </Fact>
            <Fact label="Worth">
              {get('amount') ? `${fmtMoney(Number(get('amount')))} · ${get('cycle') || 'no cycle'}` : null}
            </Fact>
          </dl>
        </div>
      </section>

      <section className="vpanel wide">
        <h4><span className="ic g-blue"><Icon name="cal" /></span>The demo</h4>
        <div className="vbody">
          <dl className="dsvfacts">
            <Fact label="Held">{stamp(b.start)}</Fact>
            <Fact label="Attendance">{s.attendance || null}</Fact>
            <Fact label="Booked">{b.bookedAt ? stamp(b.bookedAt) : null}</Fact>
            <Fact label="Came from">{b.source?.source || s.source || null}</Fact>
            <Fact label="Business">{s.business || null}</Fact>
            <Fact label="Matched by">{s.matchHow || null}</Fact>
          </dl>
          {s.demoNotes ? <p className="dsvnotes">{s.demoNotes}</p> : null}
        </div>
      </section>

      {/* The whole trail, so "why did they register" has somewhere to be
          answered rather than inferred from two dates. */}
      <section className="vpanel wide">
        <h4><span className="ic g-purple"><Icon name="zap" /></span>Journey</h4>
        <div className="vbody"><DealJourney booking={b} limit={12} /></div>
      </section>
    </Sheet>
  );
}
